/**
 * @fileoverview Context for managing mobile navigation drawer state.
 * Enables decoupled composition of the hamburger trigger and navigation sheet.
 * @module shared/contexts/MobileNavContext
 */

import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  type ReactElement,
  useMemo,
} from 'react'

interface MobileNavContextValue {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  open: () => void
  close: () => void
}

const MobileNavContext = createContext<MobileNavContextValue | null>(null)

export function MobileNavProvider({ children }: { children: ReactNode }): ReactElement {
  const [isOpen, setIsOpen] = useState(false)

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }),
    [isOpen]
  )

  return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileNav(): MobileNavContextValue {
  const context = useContext(MobileNavContext)
  if (!context) {
    throw new Error('useMobileNav must be used within MobileNavProvider')
  }
  return context
}
